import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';

export type WrhDayStatus = 'OPEN' | 'CLOSED';

export interface WrhInterval {
  id: number;
  startAt?: string;
  endAt?: string;
  startTime: string;
  endTime: string;
  kind: 'WORK' | 'REST';
  dutyCode?: string | null;
  notes?: string | null;
  isException: boolean;
  crossesMidnight?: boolean;
}

export interface WrhCrewDay {
  id: number;
  wrhDayId: number;
  crewId: number;
  crewTransactionId: number;
  crewName: string;
  rankName?: string | null;
  watchkeeper: boolean;
  workHours: number;
  restHours: number;
  compliant: boolean;
  violationCodes: string[];
  intervals: WrhInterval[];
}

export interface WrhDay {
  id: number;
  vesselId: number;
  workDate: string;
  status: WrhDayStatus;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  vessel?: { id: number; vesselName: string; vesselAbrv?: string | null };
  crewDays?: WrhCrewDay[];
  ncCount?: number;
  _count?: { crewDays: number };
  generatedCount?: number;
  rules?: {
    minRest24h: number;
    minRest7d: number;
    maxWork24h: number;
    maxRestSplits: number;
    minLongestRestSegment: number;
  };
}

export const useWrhDays = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<WrhDay> | WrhDay[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<WrhDay>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useWrhDay = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<WrhDay>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
